import Box from '@mui/material/Box';
import { Fragment, useContext, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { TableBar } from './TableBar';
import { TableBarSearch } from './TableBarSearch';
import { usePersistTableSort } from './use-persist-table-sort';
import { defaultPagination } from './use-talent-directory-params';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { ContentWrapper } from '../../../design/components/containers/ContentWrapper';
import { useTablePagination } from '../../common/data-grids/common/use-table-pagination';
import { JobOpeningsDataGrid } from '../../common/data-grids/jobs/JobsDataGrid';
import { useJobOpeningsColumns } from '../../common/data-grids/jobs/use-jobs-columns';
import { ComponentErrorBoundary } from '../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useTalentDirectoryJobExport } from '../../query-hooks/talent-directory/talent-directory';
import { useGlobalSearchDirectoryJobs } from '../../query-hooks/talent-directory/use-global-search-directory-jobs';
import { useTalentDirectoryJobFilter } from '../../query-hooks/use-talent-directory-job-filter';
import { jobPageUrl } from '../job-list/constants';

import type { FunctionComponent } from 'react';

// eslint-disable-next-line complexity
export const JobsTable: FunctionComponent = () => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();
    const searchTerm = Maybe.of(applicationState.locationSearchParams.get('search'));
    const [isSearchFieldOpen, setIsSearchFieldOpen] = useState<boolean>(searchTerm.isJust);
    const [sortOptions, saveSortOptions] = usePersistTableSort('talentDirectoryJobSort');
    const filterQuery = useTalentDirectoryJobFilter({
        enabled: true,
    });
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { isPending: isExporting, mutate: exportJobs } = useTalentDirectoryJobExport();
    const onExportButtonClick = (): void => {
        exportJobs(undefined, {
            onError: showErrorSnackbar,
            onSuccess: ({ downloadLink }) => {
                showSuccessSnackbar('success.job.exported');
                window.open(downloadLink, '_blank');
            },
        });
    };
    const { data, isFetching, isPending } = useGlobalSearchDirectoryJobs({
        filterPayload: filterQuery.data?.filterPayload ?? Maybe.nothing(),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        searchTerm,
        sortOptions,
    });
    const columns = useJobOpeningsColumns();
    const rows = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;
    const navigateToJobPage = (jobId: string): void => actions.navigateTo(jobPageUrl(jobId));
    const noDataMessage = translate('pages.global-search.no-data.job-openings');

    return (
        <Fragment>
            {filterQuery.isSuccess && (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'flex-end',
                        minHeight: '56px',
                        px: 2,
                    }}
                >
                    {isSearchFieldOpen ? (
                        <TableBarSearch onSearchBarCancelButtonClick={() => setIsSearchFieldOpen(false)} />
                    ) : (
                        <TableBar
                            filters={filterQuery.data.filters}
                            hasActiveFilters={filterQuery.data.hasActiveFilters}
                            isExporting={isExporting}
                            resetFilterValues={filterQuery.data.resetFilterValues}
                            saveFilterValues={filterQuery.data.saveFilterValues}
                            onExportButtonClick={onExportButtonClick}
                            onSearchButtonClick={() => setIsSearchFieldOpen(true)}
                        />
                    )}
                </Box>
            )}
            <ContentWrapper>
                <ComponentErrorBoundary message="errors.component.global-search.job-openings">
                    <JobOpeningsDataGrid
                        columns={columns}
                        isFetching={isFetching}
                        isPending={isPending}
                        noDataMessage={noDataMessage}
                        paginationModel={paginationModel}
                        rowCount={pagination.count}
                        rows={rows}
                        sortOptions={sortOptions}
                        onPaginationModelChange={setPaginationModel}
                        onRowClick={navigateToJobPage}
                        onSortOptionsChange={saveSortOptions}
                    />
                </ComponentErrorBoundary>
            </ContentWrapper>
        </Fragment>
    );
};
